import React, { useState, useEffect } from 'react';
import './DatabaseViewer.css';
import { apiRequest } from '../api/auth';

function DatabaseViewer() {
  const [stats, setStats] = useState(null);
  const [selectedTable, setSelectedTable] = useState('dashboard_cards');
  const [tableData, setTableData] = useState(null);
  const [tableStructure, setTableStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormData, setAddFormData] = useState({});

  const tables = [
    { name: 'users', label: 'Użytkownicy', statsKey: 'users' },
    { name: 'projects', label: 'Projekty', statsKey: 'projects' },
    { name: 'dashboard_cards', label: 'Karty Dashboard', statsKey: 'dashboardCards' },
    { name: 'employees', label: 'Pracownicy', statsKey: 'employees' },
    { name: 'rotations', label: 'Rotacje', statsKey: 'rotations' },
    { name: 'shifts', label: 'Zmiany', statsKey: 'shifts' },
    { name: 'contracts', label: 'Umowy', statsKey: 'contracts' },
    { name: 'positions', label: 'Stanowiska', statsKey: 'positions' },
    { name: 'kpi_indicators', label: 'Wskaźniki KPI', statsKey: 'kpiIndicators' },
    { name: 'kpi_data', label: 'Dane KPI', statsKey: 'kpiData' },
    { name: 'quality_data', label: 'Dane Quality', statsKey: 'qualityData' }
  ];

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (selectedTable) {
      loadTableData();
      loadTableStructure();
    }
  }, [selectedTable, limit, offset]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await apiRequest('/debug/database/stats');
      setStats(data.stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTableData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiRequest(`/debug/database/table/${selectedTable}?limit=${limit}&offset=${offset}`);
      setTableData(data);
    } catch (err) {
      setError(err.message);
      setTableData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadTableStructure = async () => {
    try {
      const data = await apiRequest(`/debug/database/table/${selectedTable}/structure`);
      setTableStructure(data.structure);
    } catch (err) {
      console.error('Błąd ładowania struktury:', err);
    }
  };

  const handlePrevPage = () => {
    if (offset >= limit) {
      setOffset(offset - limit);
    }
  };

  const handleNextPage = () => {
    if (tableData && offset + limit < tableData.total) {
      setOffset(offset + limit);
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="null-value">NULL</span>;
    }
    if (typeof value === 'object') {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (typeof value === 'string' && value.length > 100) {
      return <span title={value}>{value.substring(0, 100)}...</span>;
    }
    return String(value);
  };

  const handleEdit = (row) => {
    setEditingRow(row.id);
    setEditFormData({ ...row });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditFormData({});
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api')}/debug/database/table/${selectedTable}/${editingRow}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(editFormData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Błąd aktualizacji');
      }
      
      setEditingRow(null);
      setEditFormData({});
      await loadTableData();
      await loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten rekord?')) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api')}/debug/database/table/${selectedTable}/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Błąd usuwania');
      }
      
      await loadTableData();
      await loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setAddFormData({});
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setAddFormData({});
  };

  const handleSaveAdd = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api')}/debug/database/table/${selectedTable}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(addFormData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Błąd dodawania');
      }
      
      setShowAddForm(false);
      setAddFormData({});
      await loadTableData();
      await loadStats();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="database-viewer">
      <div className="database-viewer-header">
        <h1>📊 Podgląd Bazy Danych</h1>
        <p>SQLite Database Viewer - Przeglądaj dane z bazy danych aplikacji</p>
      </div>

      {error && (
        <div className="database-error">
          <strong>Błąd:</strong> {error}
        </div>
      )}

      {/* Statystyki */}
      {stats && (
        <div className="database-stats">
          <h2>📈 Statystyki</h2>
          <div className="stats-grid">
            {Object.entries(stats).map(([key, value]) => (
              <div key={key} className="stat-item">
                <div className="stat-label">{key}</div>
                <div className="stat-value">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wybór tabeli */}
      <div className="database-tables">
        <h2>📋 Tabele</h2>
        <div className="tables-list">
          {tables.map(table => (
            <button
              key={table.name}
              className={`table-button ${selectedTable === table.name ? 'active' : ''}`}
              onClick={() => {
                setSelectedTable(table.name);
                setOffset(0);
              }}
            >
              {table.label}
              {stats && <span className="table-count">({stats[table.statsKey] || 0})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Struktura tabeli */}
      {tableStructure && (
        <div className="database-structure">
          <h2>🏗️ Struktura tabeli: {selectedTable}</h2>
          <table className="structure-table">
            <thead>
              <tr>
                <th>Kolumna</th>
                <th>Typ</th>
                <th>NOT NULL</th>
                <th>Domyślna wartość</th>
                <th>Klucz główny</th>
              </tr>
            </thead>
            <tbody>
              {tableStructure.map((col, idx) => (
                <tr key={idx}>
                  <td><strong>{col.name}</strong></td>
                  <td>{col.type}</td>
                  <td>{col.notnull ? '✓' : ''}</td>
                  <td>{col.dflt_value || '-'}</td>
                  <td>{col.pk ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dane z tabeli */}
      {tableData && (
        <div className="database-data">
          <div className="data-header">
            <h2>📄 Dane z tabeli: {selectedTable}</h2>
            <div className="data-controls">
              <label>
                Limit:
                <input
                  type="number"
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value) || 50);
                    setOffset(0);
                  }}
                  min="1"
                  max="500"
                />
              </label>
              <div className="pagination">
                <button onClick={handlePrevPage} disabled={offset === 0}>
                  ← Poprzednia
                </button>
                <span>
                  {offset + 1} - {Math.min(offset + limit, tableData.total)} z {tableData.total}
                </span>
                <button onClick={handleNextPage} disabled={offset + limit >= tableData.total}>
                  Następna →
                </button>
              </div>
            </div>
          </div>

          <div className="data-actions">
            <button className="add-button" onClick={handleAdd}>
              + Dodaj nowy rekord
            </button>
          </div>

          {showAddForm && (
            <div className="edit-form">
              <h3>Dodaj nowy rekord</h3>
              {tableStructure && tableStructure.filter(col => col.name !== 'id' && col.name !== 'createdAt' && col.name !== 'updatedAt').map(col => (
                <div key={col.name} className="form-field">
                  <label>{col.name} ({col.type})</label>
                  {col.type.includes('TEXT') || col.type.includes('VARCHAR') ? (
                    <textarea
                      value={addFormData[col.name] || ''}
                      onChange={(e) => setAddFormData({ ...addFormData, [col.name]: e.target.value })}
                      rows={col.name === 'data' || col.name === 'projects' || col.name === 'permissions' ? 4 : 1}
                    />
                  ) : (
                    <input
                      type={col.type.includes('INT') ? 'number' : 'text'}
                      value={addFormData[col.name] || ''}
                      onChange={(e) => setAddFormData({ ...addFormData, [col.name]: e.target.value })}
                    />
                  )}
                </div>
              ))}
              <div className="form-actions">
                <button onClick={handleSaveAdd} className="save-button">Zapisz</button>
                <button onClick={handleCancelAdd} className="cancel-button">Anuluj</button>
              </div>
            </div>
          )}

          {tableData.data && tableData.data.length > 0 ? (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    {Object.keys(tableData.data[0]).map(key => (
                      <th key={key}>{key}</th>
                    ))}
                    <th>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.data.map((row, idx) => (
                    <tr key={idx}>
                      {editingRow === row.id ? (
                        <>
                          {Object.entries(row).map(([key, value]) => {
                            if (key === 'id' || key === 'createdAt' || key === 'updatedAt') {
                              return (
                                <td key={key} className={key === 'data' ? 'json-cell' : ''}>
                                  {formatValue(value)}
                                </td>
                              );
                            }
                            const col = tableStructure?.find(c => c.name === key);
                            return (
                              <td key={key}>
                                {col?.type.includes('TEXT') || col?.type.includes('VARCHAR') ? (
                                  <textarea
                                    value={editFormData[key] || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                                    rows={key === 'data' || key === 'projects' || key === 'permissions' ? 4 : 1}
                                  />
                                ) : (
                                  <input
                                    type={col?.type.includes('INT') ? 'number' : 'text'}
                                    value={editFormData[key] || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, [key]: e.target.value })}
                                  />
                                )}
                              </td>
                            );
                          })}
                          <td>
                            <button onClick={handleSaveEdit} className="save-button">Zapisz</button>
                            <button onClick={handleCancelEdit} className="cancel-button">Anuluj</button>
                          </td>
                        </>
                      ) : (
                        <>
                          {Object.entries(row).map(([key, value]) => (
                            <td key={key} className={key === 'data' ? 'json-cell' : ''}>
                              {formatValue(value)}
                            </td>
                          ))}
                          <td>
                            <button onClick={() => handleEdit(row)} className="edit-button">Edytuj</button>
                            <button onClick={() => handleDelete(row.id)} className="delete-button">Usuń</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">Brak danych w tabeli</div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading">Ładowanie...</div>
      )}
    </div>
  );
}

export default DatabaseViewer;
